import React from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Globe, Home, DollarSign, Info, User, LogOut } from 'lucide-react';
import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { PrayerRequestModal } from './PrayerRequestModal';
import UserProfileMenu from './UserProfileMenu';
import { setupNotifications } from '../lib/notification';

interface LayoutProps {
  setIsPrayerModalOpen?: (isOpen: boolean) => void;
}

export default function Layout({ setIsPrayerModalOpen }: LayoutProps) {
  const [scrolled, setScrolled] = useState(false);
  const [session, setSession] = useState<any>(null);
  const [isPrayerModalOpenState, setIsPrayerModalOpenState] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Initialize notifications
    setupNotifications().catch(err => {
      console.error('Failed to setup notifications:', err);
    });

    // Check authentication
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    }).catch(err => {
      console.error('Failed to get session:', err);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  const navigation = [
    { name: 'Perfil', href: '/profile', icon: User },
    { name: 'Doações', href: '/donations', icon: DollarSign },
    { name: 'Sobre Nós', href: '/about', icon: Info },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isHomePage = location.pathname === '/';

  // Use the prop if provided, otherwise use the local state
  const handlePrayerModalOpen = (isOpen: boolean) => {
    if (setIsPrayerModalOpen) {
      setIsPrayerModalOpen(isOpen);
    } else {
      setIsPrayerModalOpenState(isOpen);
    }
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/auth');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - Material Design style with elevation on scroll */}
      <header 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-brand-teal ${
          scrolled ? 'shadow-md' : ''
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14">
            {/* Logo on the left */}
            <Link to="/" className="flex items-center space-x-3">
              <div className="flex items-center text-white">
                <Globe className="h-6 w-6" />
                <div className="ml-2 font-bold text-lg">SdT BSB</div>
              </div>
            </Link>

            {/* Navigation items on the right */}
            <div className="flex items-center space-x-4">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className="px-3 py-2 rounded-full text-sm font-medium text-white hover:bg-brand-teal/80 transition-colors flex items-center gap-1"
                >
                  <item.icon className="h-4 w-4" />
                  <span className="hidden md:inline">{item.name}</span>
                </Link>
              ))}
              
              {session ? (
                <button
                  onClick={handleLogout}
                  className="px-3 py-2 rounded-full text-sm font-medium text-white hover:bg-brand-teal/80 transition-colors flex items-center gap-1"
                >
                  <LogOut className="h-4 w-4" />
                  <span className="hidden md:inline">Sair</span>
                </button>
              ) : (
                <Link
                  to="/auth"
                  className="px-3 py-2 rounded-full text-sm font-medium text-white hover:bg-brand-teal/80 transition-colors flex items-center gap-1"
                >
                  <User className="h-4 w-4" />
                  <span className="hidden md:inline">Entrar</span>
                </Link>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content - Material Design spacing and elevation */}
      <main className="pt-16 min-h-[calc(100vh-4rem)]">
        <Outlet />
      </main>

      {/* Footer - Material Design style */}
      <footer className="bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <div className="flex items-center justify-center text-brand-teal mb-4">
              <Globe className="h-8 w-8" />
              <span className="ml-2 font-bold text-xl">SdT BSB</span>
            </div>
            <p className="text-gray-600 text-sm">
              © 2024 Igreja Sal da Terra Brasília. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </footer>

      {/* Prayer Request Modal */}
      <PrayerRequestModal 
        isOpen={setIsPrayerModalOpen ? false : isPrayerModalOpenState} 
        onClose={() => handlePrayerModalOpen(false)} 
      />
    </div>
  );
}